import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTeam } from '../hooks/useTeam';
import Layout from '../components/Layout';

// team.vote_deadline 이 "YYYY-MM-DDTHH:MM" 또는 레거시 "HH:MM" 형식 모두 지원
function checkVotingClosed(deadline) {
  if (!deadline) return false;
  if (deadline.includes('T')) return new Date() > new Date(deadline);
  const [hh, mm] = deadline.split(':').map(Number);
  const d = new Date();
  d.setHours(hh, mm, 0, 0);
  return new Date() > d;
}

export default function Join() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { getTeamByCode, joinTeam, loading, error } = useTeam();

  const [team, setTeam] = useState(null);
  const [memberName, setMemberName] = useState('');
  const [fetchError, setFetchError] = useState('');

  // 초대코드로 팀 정보 미리 조회
  useEffect(() => {
    if (!code) return;
    getTeamByCode(code)
      .then(setTeam)
      .catch((err) => setFetchError(err.message));
  }, [code]); // eslint-disable-line

  const isVotingClosed = team ? checkVotingClosed(team.vote_deadline) : false;

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!memberName.trim()) return;
    try {
      await joinTeam(code, memberName.trim());
      // 투표 마감된 경우 결과 화면으로, 아니면 투표 화면으로
      navigate(isVotingClosed ? '/vote' : '/vote');
    } catch {
      // error는 useTeam에서 관리
    }
  };

  if (fetchError) {
    return (
      <Layout>
        <div style={s.logoMark} aria-hidden="true">🍽️</div>
        <h1 style={s.logoText}>LunchPick</h1>
        <div className="card">
          <p style={s.errorText} role="alert">❌ {fetchError}</p>
          <button className="btn-primary" onClick={() => navigate('/')}>
            홈으로 돌아가기
          </button>
        </div>
      </Layout>
    );
  }

  if (!team) {
    return (
      <Layout>
        <div style={s.logoMark} aria-hidden="true">🍽️</div>
        <h1 style={s.logoText}>LunchPick</h1>
        <p style={s.loadingText} role="status">팀 정보를 불러오는 중...</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={s.logoMark} aria-hidden="true">🍽️</div>
      <h1 style={s.logoText}>LunchPick</h1>

      <div className="card">
        <p style={s.labelText}>초대받은 팀</p>
        <h2 style={s.teamName}>{team.name}</h2>

        {isVotingClosed && (
          <div style={s.closedNotice} role="alert">
            🔒 이 팀의 투표가 이미 마감됐어요.<br />
            이름을 입력하고 참여하면 결과를 확인할 수 있어요.
          </div>
        )}

        <form onSubmit={handleJoin} style={s.form} aria-label="팀 참여 폼">
          <label style={s.srOnly} htmlFor="join-member-name">내 이름</label>
          <input
            id="join-member-name"
            className="input"
            placeholder="내 이름을 입력하세요"
            value={memberName}
            onChange={(e) => setMemberName(e.target.value)}
            autoFocus
            required
            autoComplete="off"
          />
          {error && <p style={s.errMsg} role="alert">{error}</p>}
          <button className="btn-primary" type="submit" disabled={loading}>
            {loading ? '참여 중...' : isVotingClosed ? '결과 확인하기' : `${team.name} 팀 참여하기`}
          </button>
        </form>
      </div>
    </Layout>
  );
}

const s = {
  logoMark: { fontSize: '3.2rem', lineHeight: 1, marginBottom: '0.4rem' },
  logoText: {
    margin: '0 0 1.5rem', fontSize: '2.4rem', fontWeight: '800',
    color: '#ffffff', letterSpacing: '-0.02em',
  },

  labelText: {
    color: 'var(--text-muted)', margin: '0 0 0.25rem', fontSize: '0.85rem',
  },
  teamName: {
    margin: '0 0 1rem', fontSize: '1.5rem', fontWeight: '800', color: 'var(--text-primary)',
  },

  closedNotice: {
    fontSize: '0.85rem',
    color: '#fbbf24',
    background: 'rgba(251,191,36,0.1)',
    border: '1px solid rgba(251,191,36,0.25)',
    borderRadius: 'var(--radius-sm)',
    padding: '0.7rem 0.9rem',
    marginBottom: '0.75rem',
    lineHeight: 1.6,
  },

  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  errMsg: { margin: 0, fontSize: '0.84rem', color: '#fca5a5' },
  errorText: {
    color: '#fca5a5', fontSize: '0.95rem', textAlign: 'center',
    marginBottom: '1rem',
  },
  loadingText: {
    color: 'var(--text-muted)', fontSize: '0.95rem',
    animation: 'pulse 1.5s infinite',
  },

  /* 스크린리더 전용 */
  srOnly: {
    position: 'absolute', width: '1px', height: '1px',
    padding: 0, margin: '-1px', overflow: 'hidden',
    clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0,
  },
};
